import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchGoogleCalendarEvents, CalendarEvent } from '../services/googleCalendarService';

const POLLING_INTERVAL = 60000;
const MAX_RETRIES = 3;
const RETRY_DELAY = 5000;
const MIN_FETCH_GAP = 5000;

export function useGoogleCalendar(calendarUrl: string) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchEvents = useCallback(async (isRetry = false) => {
    if (isFetchingRef.current) return;

    const now = Date.now();
    if (now - lastFetchTimeRef.current < MIN_FETCH_GAP && lastFetchTimeRef.current > 0) return;

    if (!calendarUrl) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      isFetchingRef.current = true;
      setError(null);

      const calendarEvents = await fetchGoogleCalendarEvents(calendarUrl);

      if (calendarEvents.length > 0) {
        setEvents(calendarEvents);
      }
      retryCountRef.current = 0;
      lastFetchTimeRef.current = Date.now();
    } catch (err) {
      console.error('Error fetching calendar events:', err);

      if (isRetry && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current += 1;
        retryTimerRef.current = setTimeout(() => fetchEvents(true), RETRY_DELAY);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to fetch calendar events');
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [calendarUrl]);

  useEffect(() => {
    setLoading(true);
    retryCountRef.current = 0;
    fetchEvents(true);

    const intervalId = setInterval(() => {
      if (Date.now() - lastFetchTimeRef.current >= POLLING_INTERVAL) {
        fetchEvents(false);
      }
    }, POLLING_INTERVAL);

    return () => {
      clearInterval(intervalId);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
    };
  }, [fetchEvents]);

  const refetch = useCallback(() => {
    retryCountRef.current = 0;
    return fetchEvents(true);
  }, [fetchEvents]);

  return { events, loading, error, refetch };
}
